import { Injectable } from "@angular/core";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import { AgAddressesComponent } from "@shared/components/grid/ag-cells/common/ag-addresses/ag-addresses.component";
import {
  AgEntitySecurityholderComponent
} from "@shared/components/grid/ag-cells/common/ag-entity-securityholder/ag-entity-securityholder.component";
import {
  AgYesNoStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-yes-no-status/ag-yes-no-status.component";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { getCommonActionCellColDef } from "@shared/utils/grid/get-common-column-chooser-params";
import { CellClickedEvent, ColDef, ICellRendererParams } from "ag-grid-community";
import { ValueGetterParams } from "ag-grid-enterprise";
import { nonBeneficialOwnerLabel } from "../../../../forms/asic-forms/shares/constants/non-beneficial-owner-label";

@Injectable()
export class EntitySecurityholdersGridService extends BaseGridService {
  setColDefsWithOpenShareholderActions(actionColumn: ColDef, pendingButtonState: boolean,  openSecurityholdersProfile: Function) {
    const colDefs: ColDef[] = [
      {
        headerName: 'Name',
        field: 'record.fullName',
        flex: 3,
        cellRendererParams: { pendingButtonState },
        minWidth: 200,
        autoHeight: true,
        cellClass: 'cursor-pointer',
        sortable: false,
        onCellClicked: (event: CellClickedEvent<SecurityholderViewModel>) => openSecurityholdersProfile(event.data!),
        cellRenderer: AgEntitySecurityholderComponent,
      },
      {
        headerName: 'Beneficially Owned',
        field: 'isBeneficialOwner',
        flex: 1,
        cellClass: 'spanned-cell',
        cellRenderer: AgYesNoStatusComponent,
        minWidth: 50,
        sortable: false,
      },
      {
        headerName: 'Beneficial Owner',
        field: 'beneficialOwner',
        flex: 3,
        cellClass: 'spanned-cell',
        cellRenderer: (params: ICellRendererParams<SecurityholderViewModel>) => {
          if (params.data?.isBeneficialOwner) {
            return '';
          }

          const beneficialOwnerNames = Object.entries(params.data?.beneficialOwner ?? {})
            .flatMap(([id, owner]) => owner.map((a) => a.details?.beneficialOwner?.trim() ?? ''))
            .filter(Boolean)
            .filter((name, index, names) => !names.find((n, i) => n == name && i > index));

          if (!beneficialOwnerNames.length) {
            return `<span><div class="d-inline rounded px-2 py-1 color-gray-700 bg-gray-200">${ nonBeneficialOwnerLabel }</div></span>`;
          }

          return `<span class="d-inline-block align-middle line-height-20">${ beneficialOwnerNames.map((name) => `<div>${ name }</div>`).join('') }</span>`;
        },
        minWidth: 200,
        sortable: false,
      },
      {
        headerName: 'Share Class',
        field: 'holding',
        flex: 1,
        minWidth: 50,
        sortable: false,
      },
      {
        headerName: '# of Shares',
        field: 'number',
        flex: 1,
        cellRendererParams: { pendingButtonState },
        cellRenderer: (params: ICellRendererParams<SecurityholderViewModel> & { pendingButtonState: boolean }) => {
          if (params.pendingButtonState && params.data?.number != params.data?.previousNumber) {
            return `
                <div class="line-height-1" style="display: inline-block; vertical-align: middle">
                    <div class="color-red text-line-through">${ params.data?.previousNumber ?? '-' }</div>
                    <div class="color-green">${ params.data?.number ?? '-' }</div>
                </div>
            `;
          }

          return params.data?.previousNumber ?? '-';
        },
        minWidth: 50,
        sortable: false,
      },
      {
        headerName: 'Fully Paid',
        field: 'fullyPaid',
        flex: 1,
        cellRenderer: AgYesNoStatusComponent,
        minWidth: 50,
        sortable: false,
      },
      {
        headerName: 'Address',
        field: 'addresses',
        autoHeight: true,
        valueGetter: (params: ValueGetterParams<SecurityholderViewModel>) => params.data?.addresses ?? [],
        cellRenderer: AgAddressesComponent,
        cellClass: 'h-100 d-flex align-items-center',
        hide: true,
        sortable: false,
      },
      {
        headerName: 'Authorised Signatory',
        field: 'authorisedSignatory',
        flex: 1,
        valueGetter: (params: ValueGetterParams<SecurityholderViewModel>) => params.data?.authorisedSignatory ?? [],
        cellRenderer: (params: ICellRendererParams<SecurityholderViewModel, string[][]>) => {
          return params.value?.map((entityAuthorisedSignatories) => {
            return `<div>${ entityAuthorisedSignatories.join(', ') }</div>`;
          }).join('');
        },
        hide: true,
        sortable: false,
      },
    ];

    const actionColDef: ColDef = {
      ...getCommonActionCellColDef(colDefs),
      ...actionColumn
    };

    colDefs.push(actionColDef);

    this.colDefs.set(colDefs);
  }
}
